import React, { useState, useMemo } from 'react';
import {
  Search, Check, Minus, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Skeleton } from '../../skeletons';

/**
 * DataTable — the shared table used across the dashboard.
 *
 * Config-driven: pass `columns` + `data` and it renders a premium table with
 * optional sorting, row selection, pagination, loading shimmer and empty state.
 *
 * columns: [{
 *   key,                       // unique key + default accessor (row[key])
 *   header,                    // column heading (string or node)
 *   sortable,                  // enable click-to-sort
 *   sortAccessor(row),         // custom value used when sorting (else row[key])
 *   align: 'left'|'right'|'center',
 *   render(row, index),        // custom cell (else row[key])
 *   headerClassName, cellClassName, width,
 * }]
 *
 * Selection:  selectable, onSelectionChange(rows, keySet), renderBulkActions(rows, clear)
 * Sorting:    defaultSort {key,dir}, manualSort + onSortChange({key,dir}) for server-side
 * Paging:     page, totalPages, onPageChange, [totalItems, pageSize] for the range label
 * Toolbar:    title, count, searchValue+onSearchChange, filters (node), actions (node)
 * States:     loading, skeletonRows, emptyTitle, emptyText, emptyIcon, emptyAction
 * Rows:       rowKey(row,i), onRowClick(row), rowClassName(row)
 */

const pageList = (current, total) => {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i += 1) pages.push(i);
  } else if (current <= 3) {
    pages.push(1, 2, 3, 4, '…', total);
  } else if (current >= total - 2) {
    pages.push(1, '…', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '…', current - 1, current, current + 1, '…', total);
  }
  return pages;
};

const alignClass = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

// Small styled checkbox (supports an indeterminate look for "select all").
const TableCheckbox = ({ checked, indeterminate, onChange }) => (
  <span
    role="checkbox"
    aria-checked={indeterminate ? 'mixed' : checked}
    tabIndex={0}
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onChange(); } }}
    className={`inline-grid place-items-center w-[18px] h-[18px] rounded-md border-[1.5px] cursor-pointer transition ${
      checked || indeterminate ? 'bg-blue-600 border-blue-600' : 'bg-white dark:bg-transparent border-slate-300 dark:border-gray-600 hover:border-blue-500'
    }`}
  >
    {indeterminate ? <Minus className="w-3 h-3 text-white" strokeWidth={3} />
      : checked ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : null}
  </span>
);

const DataTable = ({
  columns = [],
  data = [],
  rowKey,
  loading = false,
  skeletonRows = 6,
  emptyTitle = 'Nothing here yet',
  emptyText = '',
  emptyIcon: EmptyIcon,
  emptyAction,
  selectable = false,
  onSelectionChange,
  renderBulkActions,
  defaultSort = null,
  manualSort = false,
  onSortChange,
  onRowClick,
  rowClassName,
  page,
  totalPages = 1,
  onPageChange,
  totalItems,
  pageSize,
  title,
  count,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters,
  actions,
  bare = false,
  className = '',
}) => {
  const [sort, setSort] = useState(defaultSort);
  const [selected, setSelected] = useState(() => new Set());

  const getKey = (row, i) => (rowKey ? rowKey(row, i) : (row && row.id != null ? row.id : i));

  // Client-side sort (skipped when manualSort — parent controls order).
  const sortedData = useMemo(() => {
    if (manualSort || !sort || !sort.key) return data;
    const col = columns.find((c) => c.key === sort.key);
    const accessor = (col && col.sortAccessor) || ((row) => row[sort.key]);
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...data].sort((a, b) => {
      let x = accessor(a); let y = accessor(b);
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'string') { x = x.toLowerCase(); y = String(y).toLowerCase(); }
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }, [data, sort, columns, manualSort]);

  const keyList = sortedData.map((r, i) => getKey(r, i));
  const selectedCount = keyList.filter((k) => selected.has(k)).length;
  const allSelected = keyList.length > 0 && selectedCount === keyList.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const selectedRows = sortedData.filter((r, i) => selected.has(getKey(r, i)));

  const notify = (set) => {
    if (onSelectionChange) onSelectionChange(sortedData.filter((r, i) => set.has(getKey(r, i))), set);
  };
  const toggleRow = (key) => {
    const n = new Set(selected);
    if (n.has(key)) n.delete(key); else n.add(key);
    setSelected(n); notify(n);
  };
  const toggleAll = () => {
    const n = new Set(selected);
    if (allSelected) keyList.forEach((k) => n.delete(k)); else keyList.forEach((k) => n.add(k));
    setSelected(n); notify(n);
  };
  const clearSelection = () => { const n = new Set(); setSelected(n); notify(n); };

  const handleSort = (col) => {
    if (!col.sortable) return;
    const dir = sort && sort.key === col.key && sort.dir === 'asc' ? 'desc' : 'asc';
    const next = { key: col.key, dir };
    if (manualSort) { if (onSortChange) onSortChange(next); }
    setSort(next);
  };

  const hasToolbar = title != null || onSearchChange || filters || actions;

  // Pagination range label.
  let rangeLabel = null;
  if (onPageChange) {
    if (totalItems != null && pageSize) {
      const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
      const end = Math.min(page * pageSize, totalItems);
      rangeLabel = <>Showing <b className="text-[#334155] font-bold">{start}–{end}</b> of <b className="text-[#334155] font-bold">{totalItems}</b></>;
    } else {
      rangeLabel = <>Page <b className="text-[#334155] font-bold">{page}</b> of <b className="text-[#334155] font-bold">{totalPages}</b></>;
    }
  }

  const searchBox = onSearchChange ? (
    <div className="relative">
      <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type="text"
        value={searchValue || ''}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        className="h-10 w-full sm:w-[260px] pl-9 pr-3 rounded-xl bg-[#F6F9FC] dark:bg-[#31373f] border border-[#EEF2F8] dark:border-gray-700 text-[13.5px] text-[#334155] dark:text-gray-200 placeholder-[#94A3B8] outline-none focus:bg-white dark:focus:bg-[#383e47] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition"
      />
    </div>
  ) : null;

  const headBg = 'bg-[#F6F9FC] dark:bg-[#20242c]';

  const shellCls = bare
    ? className
    : `bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-800 rounded-[16px] shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] overflow-hidden ${className}`;

  return (
    <div className={shellCls}>

      {/* Toolbar */}
      {hasToolbar && (
        <div className="flex items-center justify-between gap-3 p-[18px] border-b border-[#EEF2F8] dark:border-gray-800 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {title != null ? (
              <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100 flex items-center gap-2.5">
                {title}
                {count != null && (
                  <span className="text-[12px] font-bold text-blue-600 bg-[#EEF3FF] dark:bg-blue-900/20 border border-[rgba(37,99,235,0.14)] px-2 py-0.5 rounded-full tabular-nums">{count}</span>
                )}
              </h3>
            ) : (
              <>
                {searchBox}
                {filters}
              </>
            )}
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            {title != null && searchBox}
            {title != null && filters}
            {actions}
          </div>
        </div>
      )}

      {/* Bulk selection bar */}
      {selectable && selectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 px-[18px] py-2.5 bg-[#EEF3FF] dark:bg-blue-900/20 border-b border-[rgba(37,99,235,0.14)]">
          <span className="text-[13px] font-semibold text-[#1D4ED8] dark:text-blue-300">
            <b className="font-extrabold">{selectedCount}</b> selected
          </span>
          <div className="flex items-center gap-2">
            {renderBulkActions && renderBulkActions(selectedRows, clearSelection)}
            <button type="button" onClick={clearSelection} className="text-[12.5px] font-semibold text-[#64748B] dark:text-gray-400 hover:text-[#334155]">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table or empty state */}
      {loading || sortedData.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                {selectable && (
                  <th className={`w-[46px] pl-[18px] pr-0 py-3 border-b border-[#E3E9F3] dark:border-gray-800 ${headBg}`}>
                    {!loading && (
                      <TableCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                    )}
                  </th>
                )}
                {columns.map((col) => {
                  const active = sort && sort.key === col.key;
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col)}
                      style={col.width ? { width: col.width } : undefined}
                      className={`px-[18px] py-3 text-[11px] font-bold uppercase tracking-[0.05em] text-[#64748B] dark:text-gray-400 border-b border-[#E3E9F3] dark:border-gray-800 whitespace-nowrap ${headBg} ${alignClass(col.align)} ${col.sortable ? 'cursor-pointer select-none hover:text-[#334155] dark:hover:text-gray-200' : ''} ${col.headerClassName || ''}`}
                    >
                      <span className={`inline-flex items-center ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                        {col.header}
                        {col.sortable && (
                          <span className="inline-flex ml-1.5">
                            {active
                              ? (sort.dir === 'desc'
                                ? <ChevronDown className="w-3.5 h-3.5 text-blue-600" />
                                : <ChevronUp className="w-3.5 h-3.5 text-blue-600" />)
                              : <ChevronsUpDown className="w-3.5 h-3.5 text-[#94A3B8]" />}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: skeletonRows }).map((_, r) => (
                  <tr key={r} className="border-b border-[#EEF2F8] dark:border-gray-800 last:border-b-0">
                    {selectable && (
                      <td className="w-[46px] pl-[18px] pr-0 py-[15px]"><Skeleton className="w-[18px] h-[18px] rounded-md" /></td>
                    )}
                    {columns.map((col, ci) => (
                      <td key={col.key} className="px-[18px] py-[15px]">
                        {ci === 0 ? (
                          <div className="flex items-center gap-3">
                            <Skeleton className="w-[38px] h-[38px] rounded-xl shrink-0" />
                            <div className="flex-1 space-y-1.5">
                              <Skeleton className="h-3 w-2/3 rounded-md" />
                              <Skeleton className="h-2.5 w-2/5 rounded-md" />
                            </div>
                          </div>
                        ) : (
                          <Skeleton className={`h-3 rounded-md ${col.align === 'right' ? 'ml-auto w-16' : 'w-3/4'}`} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                sortedData.map((row, i) => {
                  const key = getKey(row, i);
                  const isSel = selected.has(key);
                  return (
                    <tr
                      key={key}
                      onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                      className={`group/row border-b border-[#EEF2F8] dark:border-gray-800 last:border-b-0 transition-colors ${
                        isSel ? 'bg-[#EEF3FF] dark:bg-blue-900/20 hover:bg-[#E4ECFF]' : 'hover:bg-[#F6F9FC] dark:hover:bg-[#262b33]'
                      } ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName ? rowClassName(row) : ''}`}
                    >
                      {selectable && (
                        <td className="w-[46px] pl-[18px] pr-0 py-[13px]" onClick={(e) => e.stopPropagation()}>
                          <TableCheckbox checked={isSel} onChange={() => toggleRow(key)} />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={`px-[18px] py-[13px] text-[13.5px] text-[#334155] dark:text-gray-300 align-middle ${alignClass(col.align)} ${col.cellClassName || ''}`}
                        >
                          {col.render ? col.render(row, i) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="py-14 px-5 text-center">
          {EmptyIcon && (
            <div className="w-14 h-14 rounded-2xl bg-[#F6F9FC] dark:bg-[#31373f] border border-[#EEF2F8] dark:border-gray-800 grid place-items-center text-[#94A3B8] mx-auto mb-3.5">
              <EmptyIcon className="w-6 h-6" />
            </div>
          )}
          <b className="block text-[14.5px] font-bold text-[#0B1220] dark:text-gray-100">{emptyTitle}</b>
          {emptyText && <p className="mt-1 mb-4 text-[12.5px] text-[#64748B] dark:text-gray-400">{emptyText}</p>}
          {emptyAction}
        </div>
      )}

      {/* Footer / pagination */}
      {!loading && sortedData.length > 0 && onPageChange && totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 px-[18px] py-3 border-t border-[#E3E9F3] dark:border-gray-800 flex-wrap">
          <span className="text-[12.5px] text-[#64748B] dark:text-gray-400">{rangeLabel}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="min-w-[34px] h-[34px] px-1.5 inline-flex items-center justify-center rounded-[9px] text-[#334155] dark:text-gray-300 hover:bg-[#F6F9FC] dark:hover:bg-[#31373f] disabled:opacity-40 disabled:hover:bg-transparent transition"
              title="Previous page"
            >
              <ChevronLeft className="w-[18px] h-[18px]" />
            </button>
            {pageList(page, totalPages).map((p, idx) => (
              p === '…' ? (
                <span key={`d${idx}`} className="min-w-[34px] h-[34px] inline-flex items-center justify-center text-[#94A3B8]">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  className={`min-w-[34px] h-[34px] px-1.5 inline-flex items-center justify-center rounded-[9px] text-[13px] font-semibold transition ${
                    p === page ? 'bg-blue-600 text-white' : 'text-[#64748B] dark:text-gray-400 hover:bg-[#F6F9FC] dark:hover:bg-[#31373f] hover:text-[#0B1220] dark:hover:text-gray-100'
                  }`}
                >
                  {p}
                </button>
              )
            ))}
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="min-w-[34px] h-[34px] px-1.5 inline-flex items-center justify-center rounded-[9px] text-[#334155] dark:text-gray-300 hover:bg-[#F6F9FC] dark:hover:bg-[#31373f] disabled:opacity-40 disabled:hover:bg-transparent transition"
              title="Next page"
            >
              <ChevronRight className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
